import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormLabel,
  Alert,
  LinearProgress,
  Chip,

  Divider,
  Stack,
  SelectChangeEvent,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { Iconify } from 'src/components/iconify';

// API imports
import { 
  exportBranches, 
  exportAllBranches, 
  downloadBranchFile,
  getAllBranches,
  type ExportBranchParams,
  type BranchDto
} from 'src/api/branch_api_data';
import { type CompanyDto } from 'src/api/company.api';

// Hooks
import { useBoolean } from 'src/hooks/use-boolean';

// Utils
import { getUserRoleFromToken } from 'src/utils/jwt';

interface BranchExportModalProps {
  open: boolean;
  onClose: () => void;
  currentFilters?: {
    companyId?: string;
    branchId?: string;
    searchTerm?: string;
  };
  // Mevcut verileri props olarak al
  availableBranches?: BranchDto[];
  availableCompanies?: CompanyDto[];
}

type ExportFormat = 'PDF' | 'EXCEL';
type ExportType = 'current' | 'company' | 'branch' | 'all';

interface ExportState {
  format: ExportFormat;
  exportType: ExportType;
  selectedCompanyId: string;
  selectedBranchId: string;
  fileName: string;
}

const BranchExportModal: React.FC<BranchExportModalProps> = ({
  open,
  onClose,
  currentFilters = {},
  availableBranches = [],
  availableCompanies = []
}) => {
  // States
  const [exportState, setExportState] = useState<ExportState>({
    format: 'EXCEL',
    exportType: 'current',
    selectedCompanyId: currentFilters.companyId || '',
    selectedBranchId: currentFilters.branchId || '',
    fileName: ''
  });

  const [companies, setCompanies] = useState<CompanyDto[]>([]);
  const [branches, setBranches] = useState<BranchDto[]>([]);
  const [filteredBranches, setFilteredBranches] = useState<BranchDto[]>([]);
  const [userRole, setUserRole] = useState<string>('');
  const isSuperAdmin = userRole.replace(/^ROLE_/, '') === 'SUPER_ADMIN'; // FE-6: ROLE_ önekini normalize et

  // UI States
  const loading = useBoolean();
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [progress, setProgress] = useState<number>(0);
  const branchesLoading = useBoolean();

  // Refs to track if data has been loaded to avoid infinite loops
  const branchesLoadedRef = useRef<boolean>(false);

  // Initialize user role
  useEffect(() => {
    const role = getUserRoleFromToken();
    setUserRole(role || '');
    console.log('Branch Export Modal - User Role:', role);
  }, []);

  // Extract companies from branches - API bypass
  const extractCompaniesFromBranches = useCallback(() => {
    if (branches.length === 0) return;

    try {
      // Şubelerden unique şirketleri çıkar
      const uniqueCompanies = new Map<string, CompanyDto>();
      
      branches.forEach(branch => {
        if (branch.companyId && branch.companyName && !uniqueCompanies.has(branch.companyId)) {
          uniqueCompanies.set(branch.companyId, {
            id: branch.companyId,
            name: branch.companyName,
            email: 'info@company.com', // Default values
            phone: '+90 555 000 0000',
            address: 'Türkiye',
            isActive: true,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          });
        }
      });

      const companiesArray = Array.from(uniqueCompanies.values());
      setCompanies(companiesArray);
      console.log('Branch Export - Companies extracted from branches:', companiesArray.length);
      
    } catch (err) {
      console.error('Branch Export - Company extraction error:', err);
      setCompanies([]);
    }
  }, [branches]);

  // Load branches - önce props'dan kullan, yoksa API'den yükle
  const loadBranches = useCallback(async () => {
    // Zaten yüklendiyse tekrar yükleme
    if (branchesLoadedRef.current) {
      return;
    }

    try {
      branchesLoading.onTrue();
      
      // Önce props'dan gelen verileri kontrol et
      if (availableBranches && availableBranches.length > 0) {
        setBranches(availableBranches);
        console.log('Branch Export - Using branches from props:', availableBranches.length);
        branchesLoadedRef.current = true;
        return;
      }
      
      // Props'da veri yoksa API'den yükle
      console.log('Branch Export - Loading branches from API...');
      const branchesData = await getAllBranches();
      setBranches(branchesData);
      console.log('Branch Export - Branches loaded from API:', branchesData.length);
      branchesLoadedRef.current = true;
      
    } catch (err) {
      console.error('Branch Export - Branch loading error:', err);
      
      // API hatası durumunda branches boş bırak
      setBranches([]);
      console.log('Branch Export - Branches could not be loaded');
      setError('Şubeler yüklenemedi. Lütfen daha sonra tekrar deneyin.');
      branchesLoadedRef.current = true; // Hatada da tekrar denemesin
      
    } finally {
      branchesLoading.onFalse();
    }
  }, [branchesLoading, availableBranches]);

  // Load initial data
  useEffect(() => {
    if (open) {
      // Reset loading flags to allow fresh loading
      branchesLoadedRef.current = false;
      
      loadBranches();
      
      // Set default filename with Turkish timestamp
      const now = new Date();
      const turkishDate = now.toLocaleDateString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).replace(/\./g, '_');
      const turkishTime = now.toLocaleTimeString('tr-TR', {
        hour: '2-digit',
        minute: '2-digit'
      }).replace(':', '');
      
      const defaultFileName = `subeler_${turkishDate}_${turkishTime}`;
      setExportState(prev => ({ ...prev, fileName: defaultFileName }));
      
      // Clear previous states
      setError('');
      setSuccess('');
      setProgress(0);
    } else {
      // Modal kapandığında state'leri temizle
      setCompanies([]);
      setBranches([]);
      setFilteredBranches([]);
      branchesLoadedRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]); // Sadece open değişince çalışsın

  // Extract companies when branches are loaded
  useEffect(() => {
    if (branches.length > 0) {
      extractCompaniesFromBranches();
    }
  }, [branches, extractCompaniesFromBranches]);

  // Filter branches based on selected company
  useEffect(() => {
    if (exportState.selectedCompanyId && branches.length > 0) {
      const filtered = branches.filter(branch => branch.companyId === exportState.selectedCompanyId);
      setFilteredBranches(filtered);
      console.log('Filtered branches for company:', exportState.selectedCompanyId, filtered.length);
    } else {
      setFilteredBranches(branches);
    }
  }, [exportState.selectedCompanyId, branches]);



  // Handle form field changes
  const handleFormatChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setExportState(prev => ({ ...prev, format: event.target.value as ExportFormat }));
  };

  const handleExportTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newExportType = event.target.value as ExportType;
    setExportState(prev => ({ 
      ...prev, 
      exportType: newExportType,
      // Reset selections when changing type
      selectedCompanyId: newExportType === 'current' ? currentFilters.companyId || '' : 
                        newExportType === 'company' ? '' : prev.selectedCompanyId, // company type'da reset et
      selectedBranchId: newExportType === 'current' ? currentFilters.branchId || '' : 
                       newExportType === 'branch' ? '' : prev.selectedBranchId // branch type'da reset et
    }));
  };

  const handleCompanyChange = (event: SelectChangeEvent<string>) => {
    setExportState(prev => ({ 
      ...prev, 
      selectedCompanyId: event.target.value,
      selectedBranchId: '' // Reset branch when company changes
    }));
  };

  const handleBranchChange = (event: SelectChangeEvent<string>) => {
    setExportState(prev => ({ ...prev, selectedBranchId: event.target.value }));
  };

  const handleFileNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value;
    // Remove file extension if user types it
    value = value.replace(/\.(pdf|xlsx|xls)$/i, '');
    setExportState(prev => ({ ...prev, fileName: value }));
  };

  // Validation
  const isFormValid = (): boolean => {
    if (!exportState.fileName.trim()) return false;
    
    switch (exportState.exportType) {
      case 'company':
        return !!exportState.selectedCompanyId;
      case 'branch':
        return !!exportState.selectedBranchId; // Artık sadece şube seçimi yeterli
      case 'all':
        return isSuperAdmin;
      case 'current':
      default:
        return true;
    }
  };

  // Export function
  const handleExport = async () => {
    if (!isFormValid()) {
      setError('Lütfen tüm gerekli alanları doldurun');
      return;
    }

    try {
      loading.onTrue();
      setError('');
      setSuccess('');
      setProgress(10);

      console.log('Branch Export başlatılıyor:', exportState);

      // Prepare export parameters
      const exportParams: ExportBranchParams = {
        format: exportState.format,
        fileName: exportState.fileName,
        exportType: exportState.exportType
      };

      // Add type-specific parameters
      if (exportState.exportType === 'company' && exportState.selectedCompanyId) {
        exportParams.companyId = exportState.selectedCompanyId;
      }
      
      if (exportState.exportType === 'branch' && exportState.selectedBranchId) {
        exportParams.branchId = exportState.selectedBranchId;
      }

      setProgress(30);

      // Call appropriate API based on export type
      let blob: Blob;
      if (exportState.exportType === 'all' && isSuperAdmin) {
        console.log('Super Admin - Tüm şubeler export ediliyor...');
        blob = await exportAllBranches(exportParams);
      } else {
        console.log('Normal Export - Filtrelenmiş şubeler export ediliyor...');
        blob = await exportBranches(exportParams);
      }

      setProgress(80);

      // Generate filename with extension
      const extension = exportState.format === 'PDF' ? 'pdf' : 'xlsx';
      const finalFileName = `${exportState.fileName}.${extension}`;

      // Download file
      downloadBranchFile(blob, finalFileName, exportState.format);

      setProgress(100);
      setSuccess(`${exportState.format} dosyası başarıyla indirildi!`);

      // Close modal after delay
      setTimeout(() => {
        onClose();
      }, 2000);

    } catch (err) {
      console.error('Branch Export Error:', err);
      setError(err instanceof Error ? err.message : 'Export işlemi sırasında hata oluştu');
    } finally {
      loading.onFalse();
      setTimeout(() => setProgress(0), 1000);
    }
  };

  // Get export type description
  const getExportDescription = (): string => {
    switch (exportState.exportType) {
      case 'current':
        return 'Mevcut filtreler uygulanarak şubeler export edilecek';
      case 'company': {
        const selectedCompany = companies.find(c => c.id === exportState.selectedCompanyId);
        return selectedCompany ? `"${selectedCompany.name}" şirketinin şubeleri export edilecek` : 'Şirket seçili değil';
      }
      case 'branch': {
        const selectedBranch = filteredBranches.find(b => b.id === exportState.selectedBranchId);
        return selectedBranch ? `"${selectedBranch.name}" şubesi export edilecek` : 'Şube seçili değil';
      }
      case 'all':
        return isSuperAdmin ? 'Sistemdeki tüm şubeler export edilecek (Super Admin)' : 'Bu işlem için Super Admin yetkisi gerekiyor';
      default:
        return '';
    }
  };

  // Get estimated record count
  const getEstimatedCount = (): string => {
    switch (exportState.exportType) {
      case 'current':
        return 'Mevcut filtrelere göre';
      case 'company': {
        const companyBranches = branches.filter(b => b.companyId === exportState.selectedCompanyId);
        return `~${companyBranches.length} şube`;
      }
      case 'branch':
        return '1 şube';
      case 'all':
        return `~${branches.length} şube`;
      default:
        return '';
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { 
          borderRadius: 2,
          minHeight: '600px'
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Iconify icon="eva:pin-fill" width={24} color="primary.main" />
          <Typography variant="h6" component="span">
            Şube Export
          </Typography>
        </Box>
        <Button
          onClick={onClose}
          size="small"
          sx={{ minWidth: 'auto', p: 1 }}
        >
          <Iconify icon="eva:close-fill" width={20} />
        </Button>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2 }}>
        <Stack spacing={3}>
          {/* Progress Bar */}
          {progress > 0 && (
            <Box>
              <LinearProgress 
                variant="determinate" 
                value={progress} 
                sx={{ 
                  height: 8, 
                  borderRadius: 4,
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4
                  }
                }} 
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                Export işlemi devam ediyor... %{progress}
              </Typography>
            </Box>
          )}

          {/* Error Alert */}
          {error && (
            <Alert 
              severity="error" 
              icon={<Iconify icon="eva:alert-circle-fill" width={20} />}
              onClose={() => setError('')}
              sx={{ borderRadius: 2 }}
            >
              {error}
            </Alert>
          )}

          {/* Success Alert */}
          {success && (
            <Alert 
              severity="success" 
              icon={<Iconify icon="eva:checkmark-circle-fill" width={20} />}
              sx={{ borderRadius: 2 }}
            >
              {success}
            </Alert>
          )}

          {/* Format Selection */}
          <Box>
            <FormLabel component="legend" sx={{ mb: 2, fontSize: '1rem', fontWeight: 600 }}>
              Export Formatı
            </FormLabel>
            <RadioGroup
              row
              value={exportState.format}
              onChange={handleFormatChange}
              sx={{ gap: 3 }}
            >
              <FormControlLabel
                value="EXCEL"
                control={<Radio color="primary" />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Iconify icon="vscode-icons:file-type-excel" width={20} />
                    <Typography>Excel (.xlsx)</Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="PDF"
                control={<Radio color="primary" />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Iconify icon="vscode-icons:file-type-pdf2" width={20} />
                    <Typography>PDF Belgesi</Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </Box>

          <Divider />

          {/* Export Type Selection */}
          <Box>
            <FormLabel component="legend" sx={{ mb: 2, fontSize: '1rem', fontWeight: 600 }}>
              Export Kapsamı
            </FormLabel>
            <RadioGroup
              value={exportState.exportType}
              onChange={handleExportTypeChange}
              sx={{ gap: 1 }}
            >
              <FormControlLabel
                value="current"
                control={<Radio color="primary" />}
                label="Mevcut Filtreler (Şu anda görünen şubeler)"
              />
              <FormControlLabel
                value="company"
                control={<Radio color="primary" />}
                label={
                  exportState.exportType === 'company' && exportState.selectedCompanyId 
                    ? `Şirket Bazında (${companies.find(c => c.id === exportState.selectedCompanyId)?.name || 'Belirli bir şirketin şubeleri'})`
                    : "Şirket Bazında (Belirli bir şirketin şubeleri)"
                }
              />
              <FormControlLabel
                value="branch"
                control={<Radio color="primary" />}
                label={
                  exportState.exportType === 'branch' && exportState.selectedBranchId 
                    ? `Tekil Şube (${branches.find(b => b.id === exportState.selectedBranchId)?.name || 'Belirli bir şube'})`
                    : "Tekil Şube (Belirli bir şube)"
                }
              />
              {isSuperAdmin && (
                <FormControlLabel
                  value="all"
                  control={<Radio color="primary" />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography>Tüm Şubeler</Typography>
                      <Chip 
                        label="Super Admin" 
                        size="small" 
                        color="warning" 
                        variant="outlined"
                      />
                    </Box>
                  }
                />
              )}
            </RadioGroup>
          </Box>

          {/* Company Selection */}
          {exportState.exportType === 'company' && (
            <FormControl fullWidth>
              <InputLabel>Şirket Seçin</InputLabel>
              <Select
                value={exportState.selectedCompanyId}
                onChange={handleCompanyChange}
                label="Şirket Seçin"
                disabled={false}
              >
                {companies.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Iconify icon="eva:briefcase-fill" width={16} />
                      <Typography>{company.name}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Branch Selection */}
          {exportState.exportType === 'branch' && (
            <FormControl fullWidth>
              <InputLabel>Şube Seçin</InputLabel>
              <Select
                value={exportState.selectedBranchId}
                onChange={handleBranchChange}
                label="Şube Seçin"
                disabled={branchesLoading.value}
              >
                {branches.map((branch) => (
                  <MenuItem key={branch.id} value={branch.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Iconify icon="eva:pin-fill" width={16} />
                      <Typography>{branch.name}</Typography>
                      {branch.companyName && (
                        <Typography variant="caption" color="text.secondary">
                          ({branch.companyName})
                        </Typography>
                      )}
                      {branch.address && (
                        <Typography variant="caption" color="text.disabled" sx={{ ml: 1 }}>
                          - {branch.address}
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Divider />

          {/* File Name */}
          <TextField
            fullWidth
            label="Dosya Adı"
            value={exportState.fileName}
            onChange={handleFileNameChange}
            placeholder="ornek_subeler_raporu"
            helperText={`Dosya uzantısı otomatik eklenecek (.${exportState.format === 'PDF' ? 'pdf' : 'xlsx'})`}
            InputProps={{
              endAdornment: (
                <Typography variant="body2" color="text.secondary">
                  .{exportState.format === 'PDF' ? 'pdf' : 'xlsx'}
                </Typography>
              )
            }}
          />

          {/* Export Summary */}
          <Box sx={{ 
            p: 2, 
            bgcolor: 'grey.50', 
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'grey.200'
          }}>
            <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              Export Özeti
            </Typography>
            <Stack spacing={1}>
              <Typography variant="body2">
                <strong>Format:</strong> {exportState.format === 'PDF' ? 'PDF Belgesi' : 'Excel Dosyası'}
              </Typography>
              <Typography variant="body2">
                <strong>Kapsam:</strong> {getExportDescription()}
              </Typography>
              <Typography variant="body2">
                <strong>Tahmini Kayıt:</strong> {getEstimatedCount()}
              </Typography>
              <Typography variant="body2">
                <strong>Dosya Adı:</strong> {exportState.fileName}.{exportState.format === 'PDF' ? 'pdf' : 'xlsx'}
              </Typography>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button 
          onClick={onClose}
          disabled={loading.value}
          variant="outlined"
          size="large"
        >
          İptal
        </Button>
        <LoadingButton
          onClick={handleExport}
          loading={loading.value}
          disabled={!isFormValid()}
          variant="contained"
          size="large"
          startIcon={<Iconify icon="eva:download-outline" width={20} />}
          loadingPosition="start"
          sx={{ minWidth: 140 }}
        >
          {loading.value ? 'Export Ediliyor...' : 'Export Et'}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default BranchExportModal; 